import React, { useState, useRef, useEffect } from 'react';
import { Building2, MapPin, ChevronDown, ShieldAlert } from 'lucide-react';
import { useBusinessUnit } from '../../hooks/useBusinessUnit';

/**
 * ADR-018 §5 — Switcher de Business Unit no AppHeader.
 *
 * Estados:
 *  - isLoading: skeleton compacto
 *  - availableUnits=[] (Improvement Gemini): badge "Acesso negado" tooltip
 *  - length===1: badge fixo (sem dropdown)
 *  - length>=2: dropdown
 *
 * Visual coerente com AppHeader (texto xs, padding compacto, slate/blue/violet).
 */
const UNIT_META = {
  lands:    { label: 'Áreas',          Icon: MapPin,   colors: 'text-blue-700 bg-blue-50 hover:bg-blue-100' },
  launches: { label: 'Empreendimentos', Icon: Building2, colors: 'text-violet-700 bg-violet-50 hover:bg-violet-100' },
};

function UnitChip({ code, withChevron = false }) {
  const meta = UNIT_META[code] || { label: code, Icon: Building2, colors: 'text-slate-700 bg-slate-100' };
  const { Icon } = meta;
  return (
    <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${meta.colors}`}>
      <Icon size={13} />
      <span className="hidden sm:inline">{meta.label}</span>
      {withChevron && <ChevronDown size={12} className="opacity-60" />}
    </span>
  );
}

export default function BusinessUnitSwitcher() {
  const { activeUnit, availableUnits, setActiveUnit, isLoading } = useBusinessUnit();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (isLoading) {
    return <div className="h-6 w-24 rounded bg-slate-100 animate-pulse" aria-label="Carregando módulo" />;
  }

  if (!availableUnits || availableUnits.length === 0) {
    return (
      <span
        className="px-2 py-1 rounded text-xs flex items-center gap-1 text-amber-800 bg-amber-50"
        title="Você não tem permissão de leitura em nenhum módulo. Solicite ao admin."
      >
        <ShieldAlert size={13} />
        <span className="hidden sm:inline">Sem acesso</span>
      </span>
    );
  }

  if (availableUnits.length === 1 || !activeUnit) {
    return <UnitChip code={activeUnit || availableUnits[0]} />;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center"
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Trocar módulo"
      >
        <UnitChip code={activeUnit} withChevron />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute top-full left-0 mt-1 min-w-[180px] bg-white border border-slate-200 rounded-md shadow-lg py-1 z-50"
        >
          {availableUnits.map((code) => {
            const meta = UNIT_META[code] || { label: code, Icon: Building2 };
            const { Icon } = meta;
            const active = code === activeUnit;
            return (
              <li key={code}>
                <button
                  type="button"
                  onClick={() => { setActiveUnit(code); setOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-slate-50 ${
                    active ? 'font-semibold text-slate-900' : 'text-slate-700'
                  }`}
                >
                  <Icon size={13} />
                  {meta.label}
                  {active && <span className="ml-auto text-[10px] text-slate-400">ativo</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
